import { TrialPlan, UserData } from 'hydra-data-types';
import { omit } from 'ts-utils';
import { getAllScreens } from '../config/AssessmentUtils';
import { chooseAssessmentConfig } from '../config/ChooseAssessmentConfig';
import { AssessmentScreen, SharedScreenName } from '../config/FlowTypes';
import { MenoScreenName } from '../config/meno/MenoConfig';
import { ActionType } from '../store/ActionTypes';
import {
  createMockUser,
  mockEmail,
  mockName,
  mockPageStackUpToAuth,
  mockPageStackUpToPayment,
  mockPageStackUpToRecap,
  mockPageStackUpToTrial,
  mockUserData,
} from '../store/MockData';
import { StoreStateInternal, store } from '../store/Store';
import { AssessmentUser, CompletedUser, UserState } from '../store/user/UserState';
import { INITIAL_STATE } from './../store/Store';

const authUserPages = [
  SharedScreenName.WorksBest,
  SharedScreenName.DownloadApp,
  SharedScreenName.Payment,
  SharedScreenName.FinishAccount,
];

/** Skips to a page with the `user` and `userData` state and auth created or updated as required. */
export async function skipToPage(page: AssessmentScreen): Promise<void> {
  const { pageStack, user, userData } = await getStoreStateForPage(page);

  // some pages require an authenticated user
  if (authUserPages.includes(page as SharedScreenName)) {
    store.dispatch({
      type: ActionType.LOGIN_USER_SUCCESS,
      user: {
        ...(omit(user, ['loading', 'error']) as CompletedUser),
        setUserPassword: false,
      },
      userData,
      method: 'emailAndPassword',
    });
  }
  store.dispatch({ type: ActionType.MERGE_USER_DATA, userData });
  store.dispatch({ type: ActionType.SET_PAGE_STACK, pageStack });
}

/** The store data that would be expected by a page (extending the current store data). */
async function getStoreStateForPage(page: AssessmentScreen): Promise<StoreStateInternal> {
  const flowConfig = chooseAssessmentConfig();
  const allPages = getAllScreens(flowConfig);

  // The mock page stack is all pages up to and including the current page
  const autoGeneratedMockPageStack = allPages.slice(0, allPages.indexOf(page) + 1);

  const current = store.getState();
  const userData = { ...mockUserData, ...current.userData } as UserData;

  const mockAuthState = (mockUser: AssessmentUser): UserState => ({
    ...mockUser,
    loading: false,
    error: undefined,
    hasPaid: false,
    setUserPassword: true,
  });

  switch (page) {
    case MenoScreenName.UserInfoRecap:
      return { ...INITIAL_STATE, userData, pageStack: mockPageStackUpToRecap };
    case SharedScreenName.Auth:
      return { ...INITIAL_STATE, userData, pageStack: mockPageStackUpToAuth };

    case SharedScreenName.Payment: {
      // The mock user is formed by completing the current user details and signing them up if needed.
      // This means we only need to complete the user if they're incomplete/`partial`
      const mockUser =
        current.user === undefined || current.user.type === 'partial'
          ? ((await createMockUser({ user: current.user, userData })) ?? current.user)
          : current.user;
      return {
        ...INITIAL_STATE,
        pageStack: mockPageStackUpToPayment,
        user: mockAuthState(mockUser),
        userData,
      };
    }

    case SharedScreenName.WorksBest: {
      // The mock user is formed by completing the current user details and signing them up if needed.
      // This means we only need to complete the user if they're incomplete/`partial`
      const mockUser =
        current.user === undefined || current.user.type === 'partial'
          ? ((await createMockUser({ user: current.user, userData })) ?? current.user)
          : current.user;

      return {
        ...INITIAL_STATE,
        pageStack: mockPageStackUpToTrial,
        user: mockAuthState(mockUser),
        userData,
        purchase: {
          ...INITIAL_STATE.purchase,
          trialPlan: TrialPlan.OneDollarTrial,
        },
      };
    }

    // This only renders the screen but doesn't actually contain any subscription data
    case SharedScreenName.DownloadApp: {
      return {
        ...INITIAL_STATE,
        pageStack: autoGeneratedMockPageStack,
        user: { ...INITIAL_STATE.user, type: 'partial', name: mockName, email: mockEmail },
        userData,
        purchase: {
          ...INITIAL_STATE.purchase,
          trialPlan: TrialPlan.OneDollarTrial,
        },
      };
    }

    case SharedScreenName.FinishAccount: {
      const mockUser =
        current.user === undefined || current.user.type === 'partial'
          ? ((await createMockUser({ user: current.user, userData })) ?? current.user)
          : current.user;
      return {
        ...INITIAL_STATE,
        pageStack: autoGeneratedMockPageStack,
        user: mockAuthState({
          ...mockUser,
          type: 'partial',
          tempPassword: 'password',
        }),
        userData,
      };
    }

    default:
      return {
        ...INITIAL_STATE,
        pageStack: autoGeneratedMockPageStack,
        user: current.user,
        userData: current.userData,
      };
  }
}
